import { usefulInfoPdfLogo as logo } from "../brand/usefulInfoPdfLogo";
/** Offline vector PDF used by Android and web: no print CSS or network renderer. */
export type UsefulInfoPdfItem = { heading?: string; label?: string; value?: string; detail?: string; links?: Array<{ label: string; url: string }> };
const colors = { paper: "0.969 0.961 0.937", green: "0.071 0.282 0.282", ink: "0.094 0.231 0.227", muted: "0.322 0.412 0.392", sage: "0.898 0.937 0.914", gold: "0.957 0.757 0.416", border: "0.847 0.886 0.863" };
function plain(s: string) { return s.replace(/[’‘]/g, "'").replace(/[“”]/g, '"').replace(/[–—→−]/g, "-").replace(/€/g, "EUR").replace(/[^\x20-\x7e\xa0-\xff\n]/g, ""); }
function literal(s: string) { return plain(s).replace(/\\/g, "\\\\").replace(/\(/g, "\\(").replace(/\)/g, "\\)").replace(/\n/g, " "); }
function bytes(s: string) { return Uint8Array.from(s, c => c.charCodeAt(0) & 255); }
function width(s: string, size: number, bold = false) {
  // Conservative Helvetica advances; ensures even URLs/long words remain inside cards.
  return [...plain(s)].reduce((sum, c) => sum + (/[@MW%]/.test(c) ? .95 : /[ilI.,' :;!|]/.test(c) ? .30 : /[A-Z]/.test(c) ? .75 : .60), 0) * size * (bold ? 1.04 : 1);
}
function wrap(s: string, max: number, size: number, bold = false) {
  const result: string[] = [];
  for (const paragraph of plain(s).split("\n")) {
    let line = "";
    for (const word of paragraph.split(/\s+/).filter(Boolean)) {
      if (line && width(line + " " + word, size, bold) > max) { result.push(line); line = ""; }
      for (const char of (line ? " " : "") + word) {
        if (width(line + char, size, bold) > max && line) { result.push(line); line = ""; }
        line += char;
      }
    }
    result.push(line);
  }
  return result;
}
type Page = { ops: string[]; links: Array<{ x: number; y: number; w: number; url: string }> };
export function buildUsefulInfoPdf(title: string, guide: UsefulInfoPdfItem[], subtitle = "Info Utili", mapsLabel = "Apri in Google Maps"): Uint8Array {
  const pages: Page[] = []; let page: Page; let tops = [0, 0]; let group = "";
  const margin = 24, gap = 10, cardWidth = 268.5, innerWidth = cardWidth - 18, bottom = 800;
  const font = 10, leading = 12;
  const rect = (x: number, y: number, w: number, h: number, color: string) => page.ops.push(`${color} rg ${x} ${842-y-h} ${w} ${h} re f`);
  const text = (s: string, x: number, y: number, size: number, bold = false, color = colors.ink) => page.ops.push(`BT /${bold ? "F2" : "F1"} ${size} Tf ${color} rg 1 0 0 1 ${x} ${842-y-size} Tm (${literal(s)}) Tj ET`);
  function newPage() {
    page = { ops: [], links: [] }; pages.push(page);
    const first = pages.length === 1, logoWidth = first ? 174 : 114, logoHeight = logoWidth * logo.height / logo.width;
    const titleX = first ? 210 : 156, titleSize = first ? 14 : 10;
    const titleLines = wrap(title, 595 - titleX - margin, titleSize, true);
    const titleTop = first ? 30 : 24;
    const headerBottom = Math.max(first ? 72 : 50, titleTop + titleLines.length * (titleSize + 3) + 10);
    rect(0, 0, 595, 842, colors.paper); rect(0, 0, 595, headerBottom, "1 1 1");
    page.ops.push(`q ${logoWidth} 0 0 ${logoHeight} ${margin} ${842-7-logoHeight} cm /Logo Do Q`);
    text(subtitle, titleX, first ? 13 : 9, 9, false, colors.muted);
    titleLines.forEach((line, i) => text(line, titleX, titleTop + i * (titleSize + 3), titleSize, true));
    rect(margin, headerBottom - 3, 44, 3, colors.gold);
    tops = [headerBottom + 8, headerBottom + 8];
    text(subtitle, margin, 820, 8, false, colors.muted); text(String(pages.length), 557, 820, 8, true, colors.muted);
  }
  function groupHeader() {
    const top = Math.max(...tops), lines = wrap(group, 515, 11.5, true), height = 9 + lines.length * 13;
    rect(margin, top, 547, height, colors.sage); rect(margin, top, 3, height, colors.gold);
    lines.forEach((line, i) => text(line, margin + 11, top + 4 + i * 13, 11.5, true));
    tops = [top + height + 5, top + height + 5];
  }
  const advance = (line: string) => line ? leading : 5;
  newPage();
  for (let itemIndex = 0; itemIndex < guide.length; itemIndex++) {
    const item = guide[itemIndex];
    if (item.heading) {
      group = item.heading;
      // Keep a heading with at least a short card instead of stranding it at the footer.
      if (Math.max(...tops) > bottom - 112) newPage();
      groupHeader(); continue;
    }
    const titleLines = wrap(item.label || "", innerWidth, 10.8, true);
    const content = wrap([item.value, item.detail].filter(Boolean).join("\n\n"), innerWidth, font);
    const links = (item.links || []).filter(link => /^https:\/\/www\.google\.com\/maps\//.test(link.url));
    const headHeight = titleLines.length * 13 + 12, linkHeight = links.length * 17;
    const fullHeight = Math.max(44, headHeight + content.reduce((sum, line) => sum + advance(line), 0) + linkHeight + 10);
    let col = tops[0] <= tops[1] ? 0 : 1;
    if (fullHeight <= 690 && fullHeight > bottom - tops[col]) { newPage(); if (group) groupHeader(); col = 0; }
    let offset = 0;
    do {
      let top = tops[col];
      if (bottom - top < headHeight + leading + linkHeight + 10) { newPage(); if (group) groupHeader(); col = 0; top = tops[col]; }
      const lines: string[] = []; let bodyHeight = 0;
      while (offset < content.length && bodyHeight + advance(content[offset]) <= bottom - top - headHeight - linkHeight - 10) {
        const line = content[offset++]; lines.push(line); bodyHeight += advance(line);
      }
      if (!lines.length && offset < content.length) throw new Error("PDF_CARD_CANNOT_FIT");
      const final = offset >= content.length;
      const height = Math.max(44, headHeight + bodyHeight + (final ? linkHeight : 0) + 10), x = margin + col * (cardWidth + gap);
      rect(x, top, cardWidth, height, colors.border); rect(x + .6, top + .6, cardWidth - 1.2, height - 1.2, "1 1 1"); rect(x, top, cardWidth, 2.5, colors.green);
      titleLines.forEach((line, i) => text(line, x + 9, top + 7 + i * 13, 10.8, true));
      let y = top + headHeight;
      lines.forEach(line => { if (line) text(line, x + 9, y, font); y += advance(line); });
      if (final) links.forEach(link => {
        y += 2; const label = link.label || mapsLabel; text(label, x + 9, y, 10, true, colors.green);
        page.links.push({ x: x + 9, y: 842 - y - 13, w: Math.min(innerWidth, width(label, 10, true)), url: link.url }); y += 15;
      });
      tops[col] = top + height + 5;
      if (!final) { col = tops[0] <= tops[1] ? 0 : 1; if (bottom - tops[col] < headHeight + leading + linkHeight + 10) { newPage(); if (group) groupHeader(); col = 0; } }
    } while (offset < content.length);
  }
  const imageStream = (hex: string) => hex + ">";
  const rgb = imageStream(logo.rgbHex), alpha = imageStream(logo.alphaHex);
  const objects: string[] = ["<< /Type /Catalog /Pages 2 0 R >>", "", "<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>", "<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold /Encoding /WinAnsiEncoding >>",
    `<< /Type /XObject /Subtype /Image /Width ${logo.width} /Height ${logo.height} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter [/ASCIIHexDecode /FlateDecode] /SMask 6 0 R /Length ${rgb.length} >>\nstream\n${rgb}\nendstream`,
    `<< /Type /XObject /Subtype /Image /Width ${logo.width} /Height ${logo.height} /ColorSpace /DeviceGray /BitsPerComponent 8 /Filter [/ASCIIHexDecode /FlateDecode] /Length ${alpha.length} >>\nstream\n${alpha}\nendstream`];
  const kids: number[] = [];
  for (const p of pages) {
    const id = objects.length + 1; kids.push(id); const stream = p.ops.join("\n") + "\n";
    const annotations = p.links.map((link, i) => `${id + 2 + i} 0 R`).join(" ");
    objects.push(`<< /Type /Page /Parent 2 0 R /MediaBox [0 0 595 842] /Resources << /Font << /F1 3 0 R /F2 4 0 R >> /XObject << /Logo 5 0 R >> >> /Contents ${id+1} 0 R /Annots [${annotations}] >>`);
    objects.push(`<< /Length ${bytes(stream).length} >>\nstream\n${stream}endstream`);
    p.links.forEach(link => objects.push(`<< /Type /Annot /Subtype /Link /Rect [${link.x} ${link.y} ${link.x+link.w} ${link.y+16}] /Border [0 0 0] /A << /S /URI /URI (${literal(link.url)}) >> >>`));
  }
  objects[1] = `<< /Type /Pages /Count ${pages.length} /Kids [${kids.map(id => `${id} 0 R`).join(" ")}] >>`;
  let pdf = "%PDF-1.4\n%\xe2\xe3\xcf\xd3\n"; const offsets = [0];
  objects.forEach((object, i) => { offsets.push(pdf.length); pdf += `${i+1} 0 obj\n${object}\nendobj\n`; });
  const xref = pdf.length;
  pdf += `xref\n0 ${objects.length+1}\n0000000000 65535 f \n${offsets.slice(1).map(x => `${String(x).padStart(10,"0")} 00000 n \n`).join("")}trailer\n<< /Size ${objects.length+1} /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF\n`;
  return bytes(pdf);
}
